import { useParams } from 'react-router-dom';
import { Button, Space, Typography } from 'antd';
import { DownloadOutlined, FullscreenExitOutlined, FullscreenOutlined } from '@ant-design/icons';
import { useEffect, useState, useRef, useCallback } from 'react';
import ChartRenderer from '../components/charts/ChartRenderer';
import SceneDashboardView from '../components/dashboard/SceneDashboardView';
import AiCameraAlertOverlay from '../components/dashboard/AiCameraAlertOverlay';
import useDashboardStore from '../stores/dashboardStore';
import { fetchPage } from '../api/pages';
import { generateHtml } from '../services/htmlExport';
import { useVisualFullscreen } from '../hooks/useVisualFullscreen';
import { resolveBoxShadow } from '../components/dashboard/shadowUtils';
import { makeWidgetClickHandler } from '../core/widgetEventDispatch';
import Watermark, { useWatermarkFlag, getWatermarkConfig } from '../components/Watermark';
import { useViewerDataLink } from '../hooks/useViewerDataLink';

const { Text } = Typography;

function readBase(comp: any, key: string, fallback: unknown) {
  if (comp?.base && comp.base[key] !== undefined) return comp.base[key];
  if (comp?.config && comp.config[key] !== undefined) return comp.config[key];
  if (comp?.style && comp.style[key] !== undefined) return comp.style[key];
  return fallback;
}

function PreviewWidget({ comp }: { comp: any }) {
  const visible = readBase(comp, 'visible', true) as boolean;
  if (!visible) return null;
  const zIndex = readBase(comp, 'zIndex', 1) as number;
  const borderRadius = readBase(comp, 'borderRadius', 4) as number;
  const borderWidth = readBase(comp, 'borderWidth', 1) as number;
  const borderColor = readBase(comp, 'borderColor', '#334155') as string;
  const boxShadow = resolveBoxShadow(comp);
  const bgColor = readBase(comp, 'bgColor', 'transparent') as string;
  const bgImage = readBase(comp, 'bgImage', '') as string;
  const bgOpacity = readBase(comp, 'bgOpacity', 1) as number;
  const rotation = Number(readBase(comp, 'rotation', 0)) || 0;
  const title = (readBase(comp, 'title', '') as string) || '';
  const titleFontSize = readBase(comp, 'titleFontSize', 14) as number;
  // 2026-10-13 修复：预览/发布同步应用标题字体
  const titleFontFamily = readBase(comp, 'titleFontFamily', '') as string;
  const titleColor = readBase(comp, 'titleColor', '#ffffff') as string;
  const titleAlign = readBase(comp, 'titleAlign', 'center') as string;
  const titlePosition = readBase(comp, 'titlePosition', 'top') as string;

  return (
    <div
      onClick={() => makeWidgetClickHandler(comp)()}
      style={{
        position: 'absolute',
        left: comp.position.x,
        top: comp.position.y,
        width: comp.size.width,
        height: comp.size.height,
        zIndex,
        borderRadius,
        border: `${borderWidth}px solid ${borderColor}`,
        boxShadow: boxShadow,
        overflow: 'hidden',
        boxSizing: 'border-box',
        transform: rotation ? `rotate(${rotation}deg)` : undefined,
      }}
    >
      <div
        style={{
          position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0,
          borderRadius: Math.max(0, borderRadius - 2),
          background: bgColor,
          backgroundImage: bgImage ? `url(${bgImage})` : undefined,
          backgroundSize: bgImage ? 'cover' : undefined,
          opacity: bgOpacity,
        }}
      />
      <div style={{ position: 'absolute', inset: 0, zIndex: 1 }}>
        <ChartRenderer component={comp} />
      </div>
      {title && (
        <div
          style={{
            position: 'absolute',
            [titlePosition === 'bottom' ? 'bottom' : 'top']: 0,
            left: 0,
            right: 0,
            zIndex: 5,
            padding: '4px 10px',
            fontSize: titleFontSize,
            fontFamily: titleFontFamily || undefined,
            color: titleColor,
            textAlign: titleAlign === 'left' ? 'left' : titleAlign === 'right' ? 'right' : 'center',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            background: 'linear-gradient(180deg, rgba(0,0,0,0.35) 0%, transparent 100%)',
          }}
        >
          {title}
        </div>
      )}
    </div>
  );
}

export default function DashboardPreview() {
  const { id } = useParams();
  const { currentPage, components, setPage } = useDashboardStore();
  const [scaleX, setScaleX] = useState(1);
  const [scaleY, setScaleY] = useState(1);
  /** 3D 场景底开关：默认关闭（2D 大屏预览纯 2D），需要 3D 合成效果时手动开启 */
  const [show3d, setShow3d] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const { isFullscreen: fullscreen, enter, toggle: toggleFullscreen } = useVisualFullscreen();
  /** D-06：免费/基础带水印；专业/企业去水印 */
  const withWatermark = useWatermarkFlag();
  // DW-P0-5：预览态数据链路（3D 底场景绑定/设备动画/构件数据），与 Viewer 同源
  useViewerDataLink(currentPage?.sceneId ?? null);

  useEffect(() => {
    if (id) {
      fetchPage(id).then((page) => setPage(page as any)).catch(() => {});
    }
  }, [id, setPage]);

  // 缩放策略：画布完全填满容器（scaleX=容器宽/画布宽, scaleY=容器高/画布高），组件贴边、无空隙、不越界，
  // 编辑/预览/全屏/发布行为一致
  const fitScale = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    if (width <= 0 || height <= 0) return;
    const pw = currentPage?.config?.width || 1920;
    const ph = currentPage?.config?.height || 1080;
    setScaleX(width / pw);
    setScaleY(height / ph);
  }, [currentPage?.config?.width, currentPage?.config?.height]);

  useEffect(() => {
    fitScale();
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(fitScale);
    ro.observe(el);
    window.addEventListener('resize', fitScale);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', fitScale);
    };
  }, [fitScale, fullscreen]);

  const handleExport = () => {
    getWatermarkConfig().then((wm) => {
      const html = generateHtml(
        currentPage?.name || '大屏',
        currentPage?.config?.width || 1920,
        currentPage?.config?.height || 1080,
        currentPage?.config?.backgroundColor || '#0a0a1a',
        components as any,
        withWatermark && wm?.enabled ? wm : null,
      );
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${currentPage?.name || 'dashboard'}.html`;
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  const pw = currentPage?.config?.width || 1920;
  const ph = currentPage?.config?.height || 1080;

  const previewStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    background: '#0a0a1a',
    display: 'flex',
    flexDirection: 'column',
    ...(fullscreen ? { position: 'fixed', inset: 0, zIndex: 9999 } : {}),
  };

  // 画布完全填满容器（scaleX/scaleY 独立拉伸），组件贴边无空隙
  const gridStyle: React.CSSProperties = {
    width: pw * scaleX,
    height: ph * scaleY,
    flexShrink: 0,
    position: 'relative',
  };
  const innerGridStyle: React.CSSProperties = {
    position: 'relative',
    width: pw,
    height: ph,
    transform: `scale(${scaleX}, ${scaleY})`,
    transformOrigin: 'top left',
  };

  return (
    <div style={previewStyle}>
      {!fullscreen && (
        <div style={{ padding: '8px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#1a1a2e', flexShrink: 0 }}>
          <Text style={{ color: '#fff' }}>{currentPage?.name || '大屏预览'} · {pw}×{ph} · 缩放 {Math.round(((scaleX + scaleY) / 2) * 100)}%</Text>
          <Space>
            <Button size="small" icon={fullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />} onClick={toggleFullscreen}>
              {fullscreen ? '退出全屏' : '全屏'}
            </Button>
            <Button
              size="small"
              type={show3d ? 'primary' : 'default'}
              onClick={() => setShow3d((v) => !v)}
            >
              3D 场景底{show3d ? '：开' : ''}
            </Button>
            <Button size="small" icon={<DownloadOutlined />} onClick={handleExport}>导出真实 HTML</Button>
          </Space>
        </div>
      )}

      <div ref={wrapRef} onClick={() => { if (!fullscreen) enter(); }} style={{
        flex: 1,
        position: 'relative',
        overflow: 'hidden',
        background: '#0a0a1a',
        cursor: fullscreen ? 'default' : 'zoom-in',
      }}>
        {currentPage?.sceneId ? (
          <SceneDashboardView
            sceneId={currentPage.sceneId}
            components={components}
            pageWidth={pw}
            pageHeight={ph}
            backgroundColor={currentPage?.config?.backgroundColor}
            backgroundImage={currentPage?.config?.backgroundImage}
            show3D={show3d}
          />
        ) : (
          <div style={gridStyle}>
            <div style={innerGridStyle} data-testid="dashboard-canvas">
              {components.map((comp) => (
                <PreviewWidget key={comp.id} comp={comp} />
              ))}
            </div>
          </div>
        )}
        {/* D-06：预览层水印（免费/基础） */}
        <Watermark show={withWatermark === true} />
      </div>

      {/* M3-M7：AI 检测告警画中画（无论是否关联 3D 场景均生效） */}
      <AiCameraAlertOverlay />
    </div>
  );
}
